import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Grid,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { Edit, Delete, Add, Refresh } from "@mui/icons-material";
import { regionAPI } from "../../../services/api/region";

const RegionTab = ({ initialSearch = "" }) => {
  const [regions, setRegions] = useState(() => {
    const saved = localStorage.getItem("regions");
    try {
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(initialSearch);

  useEffect(() => {
    if (initialSearch) setSearch(initialSearch);
  }, [initialSearch]);

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentRegion, setCurrentRegion] = useState(null);

  const initialFormState = {
    name: "",
    description: "",
    communities: [],
    size_price_map: {},
  };
  const [formData, setFormData] = useState(initialFormState);

  const fetchRegions = async () => {
    setLoading(true);
    try {
      const skip = page * rowsPerPage;
      const response = await regionAPI.getRegions({
        skip,
        limit: rowsPerPage,
        search,
      });
      setRegions(response.data);
      localStorage.setItem("regions", JSON.stringify(response.data));
      setError(null);
    } catch (err) {
      console.error("Fehler beim Abrufen der Regionen", err);
      setError("Fehler beim Abrufen der Regionen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, [page, rowsPerPage, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (region = null) => {
    if (region) {
      setCurrentRegion(region);
      setFormData({
        name: region.name,
        description: region.description || "",
        communities: region.communities || [],
        size_price_map: region.size_price_map || {},
      });
    } else {
      setCurrentRegion(null);
      setFormData(initialFormState);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRegion(null);
    setFormData(initialFormState);
  };

  const handleDeleteClick = (region) => {
    setCurrentRegion(region);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await regionAPI.deleteRegion(currentRegion.id);
      fetchRegions();
      setOpenDeleteDialog(false);
      setCurrentRegion(null);
    } catch (err) {
      console.error("Fehler beim Löschen der Region", err);
      setError("Fehler beim Löschen der Region");
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCommunitiesChange = (communities) => {
    setFormData((prev) => ({
      ...prev,
      communities,
    }));
  };

  const handleSizePriceMapChange = (size_price_map) => {
    setFormData((prev) => ({
      ...prev,
      size_price_map,
    }));
  };

  const handleSubmit = async () => {
    try {
      if (currentRegion) {
        await regionAPI.updateRegion(currentRegion.id, formData);
      } else {
        await regionAPI.createRegion(formData);
      }
      fetchRegions();
      handleCloseDialog();
    } catch (err) {
      console.error("Fehler beim Speichern der Region", err);
      setError("Fehler beim Speichern der Region.");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Regionen
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={handleSearchChange}
          placeholder="Suche nach Name..."
        />
        <Box>
          <Button
            variant="outlined"
            onClick={fetchRegions}
            startIcon={<Refresh />}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Region hinzufügen
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Beschreibung</TableCell>
              <TableCell>Gemeinden</TableCell>
              <TableCell>Größen-Preis Zuordnung</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {regions.map((region) => (
              <TableRow key={region.id} hover>
                <TableCell>{region.name}</TableCell>
                <TableCell>{region.description || "-"}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {(region.communities || []).map((comm, idx) => (
                      <Chip key={idx} label={comm} size="small" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  {Object.entries(region.size_price_map || {}).map(
                    ([size, price]) => (
                      <div key={size}>
                        {size}m²: {price} €
                      </div>
                    ),
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(region)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(region)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {regions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Keine Regionen gefunden.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={-1}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Zeilen pro Seite:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
        }
      />

      {/* Edit/Create Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentRegion ? "Region bearbeiten" : "Neue Region erstellen"}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontSize: "1rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Grundinformationen
                </Typography>
                <Grid
                  container
                  spacing={2}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Beschreibung"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      multiline
                      rows={3}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Communities and Size-Price Mapping side by side */}
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  {/* Communities - Left Side */}
                  <Grid item xs={6}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontSize: "1rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Gemeinden
                    </Typography>
                    <CommunitiesEditor
                      communities={formData.communities}
                      onChange={handleCommunitiesChange}
                    />
                  </Grid>

                  {/* Size-Price Mapping - Right Side */}
                  <Grid item xs={6}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontSize: "1rem",
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Größen-Preis Zuordnung
                    </Typography>
                    <SizePriceMapEditor
                      sizePriceMap={formData.size_price_map}
                      onChange={handleSizePriceMapChange}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSubmit} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Region löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie die Region "{currentRegion?.name}" löschen
            möchten?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Abbrechen</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Component for editing communities
const CommunitiesEditor = ({ communities, onChange }) => {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    if (inputValue.trim() && !communities.includes(inputValue.trim())) {
      onChange([...communities, inputValue.trim()]);
      setInputValue("");
    }
  };

  const handleDelete = (comm) => {
    onChange(communities.filter((c) => c !== comm));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: 2 }}>
        <TextField
          fullWidth
          label="Neue Gemeinde hinzufügen"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ mr: 1 }}
        />
        <Button
          onClick={handleAdd}
          variant="outlined"
          startIcon={<Add />}
          sx={{ minWidth: 150 }}
        >
          Hinzufügen
        </Button>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {communities.map((comm, idx) => (
          <Chip
            key={idx}
            label={comm}
            onDelete={() => handleDelete(comm)}
            size="small"
          />
        ))}
      </Stack>
    </Box>
  );
};

// Component for editing size-price map
const SizePriceMapEditor = ({ sizePriceMap, onChange }) => {
  const [entries, setEntries] = useState(
    Object.entries(sizePriceMap).map(([size, price]) => ({
      size,
      price: price.toString(),
    })),
  );

  useEffect(() => {
    setEntries(
      Object.entries(sizePriceMap).map(([size, price]) => ({
        size,
        price: price.toString(),
      })),
    );
  }, [sizePriceMap]);

  const handleAdd = () => {
    setEntries([...entries, { size: "", price: "" }]);
  };

  const handleChange = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    setEntries(newEntries);
    updateMap(newEntries);
  };

  const handleDelete = (index) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    updateMap(newEntries);
  };

  const updateMap = (ents) => {
    const map = {};
    ents.forEach(({ size, price }) => {
      if (size.trim()) {
        map[size.trim()] = parseFloat(price) || 0;
      }
    });
    onChange(map);
  };

  return (
    <Box>
      {entries.map((entry, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
          <Grid item xs={5}>
            <TextField
              fullWidth
              label="Größe (m²)"
              value={entry.size}
              onChange={(e) => handleChange(index, "size", e.target.value)}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              fullWidth
              label="Preis (€)"
              type="number"
              value={entry.price}
              onChange={(e) => handleChange(index, "price", e.target.value)}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={() => handleDelete(index)} color="error">
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button onClick={handleAdd} variant="outlined" startIcon={<Add />}>
        Eintrag hinzufügen
      </Button>
    </Box>
  );
};

export default RegionTab;
